import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Play, Eye, RefreshCw, Activity, TrendingUp, Calendar, Zap } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AnnouncementAutomation = () => {
  const [previews, setPreviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [stats, setStats] = useState(null);
  const [rotationConfig, setRotationConfig] = useState(null);

  useEffect(() => {
    fetchRotationConfig();
  }, []);

  const fetchRotationConfig = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/announcements/automation/rotation-config`);
      setRotationConfig(response.data);
    } catch (error) {
      console.error('Error fetching rotation config:', error);
    }
  };

  const handlePreview = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/announcements/automation/preview`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('admin_token')}`
          }
        }
      );
      setPreviews(response.data.announcements);
    } catch (error) {
      console.error('Error previewing announcements:', error);
      alert('Failed to preview announcements');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!window.confirm('This will automatically create announcements. Continue?')) {
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/announcements/automation/generate`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('admin_token')}`
          }
        }
      );
      setStats(response.data.stats);
      alert('Automation completed successfully!');
      await fetchRotationConfig();
    } catch (error) {
      console.error('Error generating announcements:', error);
      alert('Failed to generate announcements');
    } finally {
      setGenerating(false);
    }
  };

  const getThemeColor = (theme) => {
    switch (theme) {
      case 'colorful':
        return 'bg-gradient-to-r from-purple-500 to-pink-500';
      case 'festive':
        return 'bg-gradient-to-r from-orange-500 to-red-500';
      case 'professional':
        return 'bg-gradient-to-r from-blue-600 to-indigo-600';
      default:
        return 'bg-gray-600';
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">🤖 Announcement Automation</h1>
        <p className="text-gray-600">
          AI-powered announcement generation based on real-time metrics, time triggers, and business events
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Active Announcements</p>
              <p className="text-2xl font-bold text-gray-800">
                {rotationConfig?.total_announcements || 0}
              </p>
            </div>
            <Activity className="text-purple-500" size={32} />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Rotation Interval</p>
              <p className="text-2xl font-bold text-gray-800">
                {rotationConfig?.rotation_interval || 0}s
              </p>
            </div>
            <RefreshCw className="text-blue-500" size={32} />
          </div>
        </div>

        {stats && (
          <>
            <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Created (Last Run)</p>
                  <p className="text-2xl font-bold text-gray-800">{stats.created}</p>
                </div>
                <TrendingUp className="text-green-500" size={32} />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6 border-l-4 border-orange-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Generated</p>
                  <p className="text-2xl font-bold text-gray-800">{stats.total_generated}</p>
                </div>
                <Zap className="text-orange-500" size={32} />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Action Buttons */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Automation Controls</h2>
        <div className="flex flex-wrap gap-4">
          <button
            onClick={handlePreview}
            disabled={loading}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Eye size={20} />
            {loading ? 'Loading...' : 'Preview Announcements'}
          </button>

          <button
            onClick={handleGenerate}
            disabled={generating}
            className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Play size={20} />
            {generating ? 'Generating...' : 'Generate Now'}
          </button>

          <button
            onClick={fetchRotationConfig}
            className="flex items-center gap-2 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            <RefreshCw size={20} />
            Refresh Stats
          </button>
        </div>

        <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-sm text-blue-800">
            <strong>ℹ️ Note:</strong> Automation runs automatically every hour. Use "Generate Now" to trigger an immediate run, or "Preview" to see what would be generated without creating announcements.
          </p>
        </div>
      </div>

      {/* Current Active Announcements */}
      {rotationConfig?.announcements && rotationConfig.announcements.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">📢 Currently Active Announcements</h2>
          <div className="space-y-3">
            {rotationConfig.announcements.map((ann, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{index + 1}</span>
                  <div>
                    <p className="font-semibold text-gray-800">{ann.title}</p>
                    <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                      {ann.type}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">Priority: {ann.priority}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Preview Results */}
      {previews.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            👁️ Preview: {previews.length} Announcement{previews.length > 1 ? 's' : ''} Would Be Generated
          </h2>
          <div className="space-y-4">
            {previews.map((announcement, index) => (
              <div
                key={index}
                className={`${getThemeColor(announcement.theme)} text-white rounded-lg p-6 shadow-lg`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-3xl">{announcement.icon}</span>
                      <h3 className="text-xl font-bold">{announcement.title}</h3>
                      <span className="ml-2 px-2 py-1 bg-white/20 rounded text-xs">
                        {announcement.type}
                      </span>
                    </div>
                    <p className="mb-4 text-white/90">{announcement.message}</p>
                    <div className="flex flex-wrap gap-3 text-sm">
                      {announcement.cta_text && (
                        <div className="flex items-center gap-1">
                          <span className="font-semibold">CTA:</span>
                          <span>{announcement.cta_text}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <span className="font-semibold">Priority:</span>
                        <span>{announcement.priority}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="font-semibold">Dismiss:</span>
                        <span>{announcement.dismiss_behavior}</span>
                      </div>
                      {announcement.rotation_interval && (
                        <div className="flex items-center gap-1">
                          <RefreshCw size={14} />
                          <span>{announcement.rotation_interval}s</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Automation Features Info */}
      <div className="mt-6 bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">✨ Automation Features</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg p-4">
            <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
              <TrendingUp size={18} className="text-purple-600" />
              Metrics-Based
            </h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Success stories (weekly visa approvals)</li>
              <li>• Milestones (customer count achievements)</li>
              <li>• Trending services (most popular)</li>
              <li>• Conversion rate highlights</li>
            </ul>
          </div>

          <div className="bg-white rounded-lg p-4">
            <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
              <Calendar size={18} className="text-blue-600" />
              Time-Based
            </h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Weekend availability reminders</li>
              <li>• Seasonal campaigns (festive offers)</li>
              <li>• Time-of-day messages (morning/evening)</li>
              <li>• Day-specific promotions</li>
            </ul>
          </div>

          <div className="bg-white rounded-lg p-4">
            <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
              <Zap size={18} className="text-green-600" />
              Event-Based
            </h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• New service launches</li>
              <li>• Document checklist reminders</li>
              <li>• Policy update notifications</li>
              <li>• Special event announcements</li>
            </ul>
          </div>

          <div className="bg-white rounded-lg p-4">
            <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
              <Activity size={18} className="text-orange-600" />
              Smart Management
            </h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Auto-rotation (max 5 active)</li>
              <li>• Priority-based display</li>
              <li>• Duplicate prevention</li>
              <li>• Automatic expiry cleanup</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementAutomation;
